<template>
  <BaseDialog
    :is-open="isOpen"
    size="lg"
    @close="emit('close')"
  >
    <div class="w-[50vw] py-4">
      <div class="mb-4 flex w-full items-center">
        <div class="flex-1" />
        <SearchInput v-model="search" />
        <div
          class="flex flex-1 cursor-pointer items-center justify-end"
          @click="emit('close')"
        >
          <IconX class="text-background-fg/70" />
        </div>
      </div>

      <BaseTable
        :rows="data?.items ?? []"
        :columns="columns"
        :is-loading="isLoading"
      >
        <template #cell-actions="{ row }">
          <button
            class="text-sm font-medium text-info/87 disabled:pointer-events-none disabled:text-background-fg/30"
            :disabled="disableMerchantPredicate?.(row)"
            @click="emit('selectMerchant', row)"
          >
            Add
          </button>
        </template>
      </BaseTable>
      <div class="flex justify-end">
        <BasePagination
          v-model="page"
          :page-count="pageCount"
        />
      </div>
    </div>
  </BaseDialog>
</template>

<script setup lang="ts">
import type { Merchant } from "@/services/merchants/listMerchants"
import type { TableColumn } from "./BaseTable.vue"

defineProps<{
  isOpen: boolean
  disableMerchantPredicate?: (merchant: Merchant) => boolean
}>()

const emit = defineEmits<{
  (event: "selectMerchant", value: Merchant): void
  (event: "close"): void
}>()

const search = ref("")
const debouncedSearch = debouncedRef(search, 300)

watch(debouncedSearch, () => resetPagination(), {
  deep: true,
})

const { page, pageCount, pagination, resetPagination } =
  usePagination({
    count: computed(() => data.value?.count || 0),
    limit: 10,
  })

const query = reactiveComputed(() => ({
  search: debouncedSearch.value,
  searchFields: [
    "pax_merchant_id",
    "merchant_name",
  ] as const,
  ...pagination.value,
}))

const { data, isLoading } = useMerchants(query)

type ColumnId =
  | "merchantName"
  | "merchantId"
  | "merchantGroupName"
  | "currency"
  | "actions"

const columns: Array<TableColumn<Merchant, ColumnId>> = [
  {
    id: "merchantName",
    header: "Merchant Name",
    getCellValue: (row) => row.merchantName,
  },
  {
    id: "merchantId",
    header: "MID",
    getCellValue: (row) => row.merchantId,
  },
  {
    id: "merchantGroupName",
    header: "Merchant Group",
    getCellValue: (row) => row.merchantGroup?.name,
  },
  {
    id: "currency",
    header: "Currency",
    getCellValue: (row) => row.currency,
  },
  {
    id: "actions",
    autoWidth: true,
  },
]
</script>
